import type { ShopperBasketsTypes } from 'commerce-sdk-isomorphic'

export type createBasketWithItemOpts = {
  productId: string
  specialOrderSKU?: string
  size?: string
  engravingText?: string
  optionItems?: Array<ShopperBasketsTypes.OptionItem>
}

export type AddItemToBasketOpts = createBasketWithItemOpts & {
  basketId: string
}

export type UpdateBasketItemOpts = Omit<createBasketWithItemOpts, 'productId'> & {
  productId?: string
  basketId: string
  itemId: string
}

export default function useBasket() {
  const nuxtApp = useNuxtApp()
  const locale = nuxtApp.$i18n.locale.value === 'fr' ? localeConfig[nuxtApp.$i18n.locale.value].code : ''
  // SFCC expects the parameter omitted outside French; an empty string is not a valid locale.
  const localeParams: { locale?: string } = locale ? { locale } : {}

  async function createBasketWithItem(opts: createBasketWithItemOpts) {
    const { getAuthHeaders, shopperBaskets } = useShopper()
    try {
      return await shopperBaskets.createBasket({
        headers: await getAuthHeaders(),
        body: {
          productItems: [
            {
              quantity: 1,
              productId: opts.productId,
              c_specialOrderSKU: opts.specialOrderSKU,
              c_ringSize: opts.size,
              c_engravingText: opts.engravingText,
              optionItems: opts.optionItems,
            },
          ],
        },
        parameters: {
          ...localeParams,
        },
      })
    } catch (e) {
      return await salesforceErrorHandler(e)
    }
  }

  async function addItemToBasket(opts: AddItemToBasketOpts) {
    const { getAuthHeaders, shopperBaskets } = useShopper()
    try {
      const response = await shopperBaskets.addItemToBasket({
        headers: await getAuthHeaders(),
        parameters: {
          basketId: opts.basketId,
          ...localeParams,
        },
        body: [
          {
            quantity: 1,
            productId: opts.productId,
            c_specialOrderSKU: opts.specialOrderSKU,
            c_ringSize: opts.size,
            c_engravingText: opts.engravingText,
            optionItems: opts.optionItems,
          },
        ],
      })
      return response
    } catch (e) {
      return await salesforceErrorHandler(e)
    }
  }

  async function updateItemInBasket(opts: UpdateBasketItemOpts) {
    const {
      itemId,
      basketId,
      productId,
      specialOrderSKU: c_specialOrderSKU,
      size: c_ringSize,
      engravingText: c_engravingText,
      optionItems,
    } = opts
    const { getAuthHeaders, shopperBaskets } = useShopper()

    try {
      const response = await shopperBaskets.updateItemInBasket({
        headers: await getAuthHeaders(),
        parameters: {
          basketId,
          itemId,
          ...localeParams,
        },
        body: {
          quantity: 1,
          productId,
          c_specialOrderSKU,
          c_ringSize,
          c_engravingText,
          optionItems,
        },
      })
      return response
    } catch (e) {
      return await salesforceErrorHandler(e)
    }
  }

  async function updateItemsInBasket(basketId: string, items: Array<ShopperBasketsTypes.ProductItem>) {
    const { getAuthHeaders, shopperBaskets } = useShopper()
    try {
      const response = await shopperBaskets.updateItemsInBasket({
        headers: await getAuthHeaders(),
        parameters: {
          basketId,
          ...localeParams,
        },
        body: items,
      })
      return response
    } catch (e) {
      return await salesforceErrorHandler(e)
    }
  }

  async function removeItemFromBasket(itemId: string, basketId: string) {
    const { getAuthHeaders, shopperBaskets } = useShopper()
    try {
      const basket = await shopperBaskets.removeItemFromBasket({
        parameters: {
          itemId,
          basketId,
          ...localeParams,
        },
        headers: await getAuthHeaders(),
      })
      return basket
    } catch (e) {
      return await salesforceErrorHandler(e)
    }
  }

  async function updateBasket(basketId: string, opts: any = {}) {
    const { getAuthHeaders, shopperBaskets } = useShopper()
    try {
      const response = await shopperBaskets.updateBasket({
        headers: await getAuthHeaders(),
        parameters: {
          basketId,
          ...localeParams,
        },
        body: { ...opts },
      })
      return response
    } catch (e) {
      return await salesforceErrorHandler(e)
    }
  }

  async function createBasket() {
    const { getAuthHeaders, shopperBaskets } = useShopper()
    try {
      const response = await shopperBaskets.createBasket({
        headers: await getAuthHeaders(),
        body: {},
        parameters: {
          ...localeParams,
        },
      })
      return response
    } catch (e) {
      return await salesforceErrorHandler(e)
    }
  }

  // Merges basket without Customer PII
  async function mergeBasket() {
    const { getAuthHeaders, shopperBaskets } = useShopper()
    try {
      const response = await shopperBaskets.mergeBasket({
        headers: await getAuthHeaders(),
        parameters: {
          ...localeParams,
        },
      })

      await patchEngraving(response)
      return response
    } catch (e) {
      if (e instanceof Error && (e as any).response?.status === 409) {
        const response = await transferBasket()
        await patchEngraving(response)
        return response
      }
      return await salesforceErrorHandler(e)
    }
  }

  /* FIXME: The following should be treated as a work around and we should look to remove in the near future by fixing the engraving options item
   * there has already been a spike investigation into this and the solution was to only have 2 options for engraving. either yes or no
   * yes would incur a cost of which we would then setup a discount for the engraving product and no obviously would mean no engraving.
   * this would allow us to remove this code as if a member is logged in the cart would auto apply the product option promotion
   * thus wiping the engraving charge. This is actually already setup in dev however there are repercussions in e2e with comestri as it
   * currently cannot acocunt for a discount line on a product option, so until we address that piece of tech debt we will need to keep the
   * below code as a work around.
   * */
  async function patchEngraving(basket: ShopperBasketsTypes.Basket) {
    const engravingProductsToUpdate: UpdateBasketItemOpts[] = []
    basket?.productItems?.forEach((item) => {
      if (!item.optionItems?.length) return
      item.optionItems.forEach((optionItem) => {
        // option '3' is engraving yes (non-member) and '2' is engraving yes (member)
        if (optionItem.optionId === 'engraving' && optionItem.optionValueId === '3' && useUserStore().isLoggedIn) {
          engravingProductsToUpdate.push({
            basketId: basket.basketId || '',
            itemId: item.itemId || '',
            productId: item.productId,
            engravingText: item.c_engravingText,
            optionItems: [
              {
                optionId: 'engraving',
                optionValueId: '2',
              },
            ],
          })
        }
      })
    })
    engravingProductsToUpdate.forEach(async (item, index, arr) => {
      const res = await updateItemInBasket(item)
      if (index === arr.length - 1) {
        useBasketsStore().setBasket(res)
      }
    })
  }

  // Transfers basket with Customer PII
  async function transferBasket() {
    const { getAuthHeaders, shopperBaskets } = useShopper()
    try {
      const response = await shopperBaskets.transferBasket({
        headers: await getAuthHeaders(),
        parameters: {
          ...localeParams,
        },
      })
      return response
    } catch (e) {
      return await salesforceErrorHandler(e)
    }
  }

  async function getCustomerBaskets() {
    const { getAuthHeaders, shopperCustomers } = useShopper()
    try {
      const customerId = useUserStore().user?.customerId ?? useUserStore().guestUser?.customerId
      if (!customerId) {
        return
      }
      const response = await shopperCustomers.getCustomerBaskets({
        headers: await getAuthHeaders(),
        parameters: {
          customerId,
        },
      })

      if (response?.baskets) {
        // TODO: Validate response
        // @ts-ignore
        await useBasketsStore().setBaskets(response.baskets)
      }
      return response
    } catch (e) {
      return await salesforceErrorHandler(e)
    }
  }

  async function updateBasketWithProductData(basket: ShopperBasketsTypes.Basket) {
    try {
      const { getAuthHeaders, shopperBaskets } = useShopper()
      if (!basket?.basketId) return basket

      // Only need to make this additional api call if we are in French locale as the default basket is returned in english
      // have to do this here as the current way we fetch baskets through shopperCustomer does not support locale parameter
      const localisedBasket =
        locale === 'fr-CA'
          ? await shopperBaskets.getBasket({
              headers: await getAuthHeaders(),
              parameters: {
                basketId: basket.basketId,
                ...localeParams,
              },
            })
          : basket

      if (!localisedBasket?.productItems?.length) return localisedBasket

      const updatedBasket = { ...localisedBasket }
      const productItemsWithData = await mergeProductItemsWithProductData(localisedBasket.productItems)
      updatedBasket.productItems = productItemsWithData
      await handleProductItemsStatusChanges(updatedBasket, localisedBasket)
      return updatedBasket
    } catch (e) {
      return await salesforceErrorHandler(e)
    }
  }

  // Handle the edge case that a product item becomes MTO or vice versa due to stock changes
  async function handleProductItemsStatusChanges(
    updatedBasket: ShopperBasketsTypes.Basket,
    originalBasket: ShopperBasketsTypes.Basket,
  ) {
    for (const item of updatedBasket?.productItems || []) {
      const isSOProduct = item.productCustomAttributes?.c_mhj_isSpecialOrderProduct
      const originalItem = originalBasket?.productItems?.find(({ productId }) => productId === item.productId)

      if (isSOProduct) {
        const productSizeOptions: processedSizeOptions[] = item.productCustomAttributes.c_mhj_processedSizeOptions

        if (productSizeOptions) {
          const size =
            item.c_ringSize ?? item.productCustomAttributes.c_length ?? item.productCustomAttributes.c_bangleSize
          // Match by size first, fall back to matching by variant SKU (productId) in case of size format mismatches
          const productVariant =
            productSizeOptions.find((variant) => variant.isSpecialOrder && variant.size === size) ??
            productSizeOptions.find((variant) => variant.isSpecialOrder && variant.sfccVariantSKU === item.productId)
          // Defensive: use originalItem's c_specialOrderSKU if we cannot find a match in the processedSizeOptions.
          // e.g. the Ring Builder may have set specialOrderSKUs and must not be cleared.
          item.c_specialOrderSKU = productVariant?.specialOrderSKU ?? originalItem?.c_specialOrderSKU
        } else {
          item.c_specialOrderSKU = item.productId
        }
      } else {
        item.c_specialOrderSKU = undefined
      }

      const basketId = updatedBasket.basketId
      const itemId = item.itemId
      if (
        basketId &&
        itemId &&
        originalItem &&
        (originalItem.c_specialOrderSKU || item.c_specialOrderSKU) &&
        originalItem.c_specialOrderSKU !== item.c_specialOrderSKU
      ) {
        // Sync back the updated specialOrderSKU to the basket
        const response = await updateItemInBasket({
          basketId,
          itemId,
          specialOrderSKU: item.c_specialOrderSKU ?? '',
        })
        if (response && !(response instanceof Error)) {
          // Although item.c_specialOrderSKU was the source, update it from SFCC response to keep the store in sync
          const responseItem = response.productItems?.find(
            (pi: ShopperBasketsTypes.ProductItem) => pi.itemId === itemId,
          )
          if (responseItem) {
            item.c_specialOrderSKU = responseItem.c_specialOrderSKU
          }
        }
      }
    }
  }

  async function getCustomerBasketsWithImage() {
    const { getAuthHeaders, shopperCustomers } = useShopper()
    try {
      const customerId = useUserStore().user?.customerId ?? useUserStore().guestUser?.customerId
      if (!customerId) {
        return
      }
      const response = await shopperCustomers.getCustomerBaskets({
        headers: await getAuthHeaders(),
        parameters: {
          customerId,
        },
      })

      let basketsWithImage = []
      if (response && response?.baskets) {
        // Fetch productData.image and merge with basket productItems
        for (const basket of response.baskets) {
          const basketWithImage = await updateBasketWithProductData(basket as ShopperBasketsTypes.Basket)
          if (!(basketWithImage instanceof Error) && basketWithImage?.basketId) {
            basketsWithImage.push(basketWithImage) // Only add valid baskets
          }
        }
        await useBasketsStore().setBaskets(basketsWithImage)
      }

      return basketsWithImage
    } catch (e) {
      return await salesforceErrorHandler(e)
    }
  }

  async function getSalesforceBasket() {
    const existingBaskets = await getCustomerBaskets()
    if (!(existingBaskets instanceof Error || !existingBaskets) && existingBaskets.length > 0) {
      return existingBaskets![0]
    }
    const newBasket = await createBasket()
    return newBasket
  }

  async function addPaymentInstrument(paymentMethodId: string, amount: number, opts?: any) {
    const { getAuthHeaders, shopperBaskets } = useShopper()
    const { basketId, setBasket } = useBasketsStore()
    if (!basketId) {
      return
    }
    try {
      const response = await shopperBaskets.addPaymentInstrumentToBasket({
        headers: await getAuthHeaders(),
        parameters: {
          basketId,
          ...localeParams,
        },
        body: {
          paymentMethodId,
          amount,
          ...opts,
        },
      })

      await setBasket(response)

      return response
    } catch (e) {
      return await salesforceErrorHandler(e)
    }
  }

  async function removePaymentInstrument(paymentInstrumentId: string) {
    const { getAuthHeaders, shopperBaskets } = useShopper()
    const { basketId, setBasket } = useBasketsStore()
    if (!basketId) {
      return
    }
    try {
      const response = await shopperBaskets.removePaymentInstrumentFromBasket({
        headers: await getAuthHeaders(),
        parameters: {
          basketId,
          paymentInstrumentId,
          ...localeParams,
        },
      })

      await setBasket(response)

      return response
    } catch (e) {
      return await salesforceErrorHandler(e)
    }
  }

  async function updateCustomerForBasket(body: ShopperBasketsTypes.CustomerInfo) {
    const { getAuthHeaders, shopperBaskets } = useShopper()
    const { basketId, setBasket } = useBasketsStore()

    if (!basketId) {
      return
    }

    try {
      const response = await shopperBaskets.updateCustomerForBasket({
        headers: await getAuthHeaders(),
        parameters: {
          basketId,
          ...localeParams,
        },
        body,
      })

      await setBasket(response)

      return response
    } catch (e) {
      return await salesforceErrorHandler(e)
    }
  }

  async function getPaymentMethodsForBasket() {
    const { getAuthHeaders, shopperBaskets } = useShopper()
    const { basketId } = useBasketsStore()

    if (!basketId) {
      return
    }

    try {
      const response = await shopperBaskets.getPaymentMethodsForBasket({
        headers: await getAuthHeaders(),
        parameters: {
          basketId,
          ...localeParams,
        },
      })

      return response
    } catch (e) {
      return await salesforceErrorHandler(e)
    }
  }

  async function applyCoupon(coupon: string) {
    const basketStore = useBasketsStore()
    if (!basketStore.basketId) return

    const { getAuthHeaders, shopperBaskets, config } = useShopper()

    try {
      const response = await shopperBaskets.addCouponToBasket({
        headers: await getAuthHeaders(),
        parameters: {
          basketId: basketStore.basketId,
          organizationId: config.organizationId,
          siteId: config.siteId,
          ...localeParams,
        },
        body: {
          code: coupon,
        },
      })

      await basketStore.setBasket(response)

      const isCouponValid = basketStore.basket.couponItems?.find((item) => item.code === coupon)?.valid
      return isCouponValid
    } catch (e) {
      console.error('Error applying coupon:', await salesforceErrorHandler(e))
      return false
    }
  }

  async function removeCoupon(coupon: string) {
    const basketStore = useBasketsStore()
    if (!basketStore.basketId) return

    const { getAuthHeaders, shopperBaskets, config } = useShopper()
    const couponItem = basketStore.basket.couponItems?.find((item) => item.code === coupon)

    if (!couponItem?.couponItemId) {
      console.error(`Coupon ${coupon} not found in the basket`)
      return
    }

    try {
      const response = await shopperBaskets.removeCouponFromBasket({
        headers: await getAuthHeaders(),
        parameters: {
          basketId: basketStore.basketId,
          couponItemId: couponItem.couponItemId,
          organizationId: config.organizationId,
          siteId: config.siteId,
          ...localeParams,
        },
      })

      await basketStore.setBasket(response)

      const isCouponRemoved = !basketStore.basket.couponItems?.find((item) => item.code === coupon)
      return isCouponRemoved
    } catch (e) {
      console.error('Error removing coupon:', await salesforceErrorHandler(e))
      return false
    }
  }

  return {
    createBasketWithItem,
    addItemToBasket,
    getCustomerBaskets,
    getCustomerBasketsWithImage,
    updateBasketWithProductData,
    createBasket,
    mergeBasket,
    transferBasket,
    getSalesforceBasket,
    updateBasket,
    removeItemFromBasket,
    updateItemInBasket,
    updateItemsInBasket,
    addPaymentInstrument,
    removePaymentInstrument,
    updateCustomerForBasket,
    getPaymentMethodsForBasket,
    applyCoupon,
    removeCoupon,
  }
}
